/* eslint-disable @typescript-eslint/no-explicit-any */
import { CustomFilterMeta } from '@src/main/modules/messages/ui/mailbox/views/mailbox/mailbox.types';
import { User } from '@src/main/modules/user/domain/user.interface';
import { TokenProvider } from '@src/main/shared/infrastructure/entity/axios';
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

export function useMailbox<TFormattedMessage>(
  user: User | null,
  createRepository: (tokenProvider: TokenProvider) => any,
  useFormattedMessages: (
    repo: any,
    criteria: any,
  ) => {
    messages: TFormattedMessage[];
    total: number;
    isLoading: boolean;
  },
  mapFiltersToCriteriaFn: (filters: CustomFilterMeta) => any,
  initialFilters: CustomFilterMeta,
) {
  const [filters, setFilters] = useState(initialFilters);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loadingRepository, setLoadingRepository] = useState(true);
  const [repository, setRepository] = useState<any>(null);
  const [sortField, setSortField] = useState<string>('sendDate');
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  useEffect(() => {
    if (user?.token) {
      const repo = createRepository(tokenProvider);
      setRepository(repo);
      setLoadingRepository(false);
    }
  }, [createRepository, tokenProvider, user?.token]);

  useEffect(() => {
    setPageNumber(0);
  }, [filters]);

  const criteria = useMemo(
    () => ({
      ...mapFiltersToCriteriaFn(filters),
      page: pageNumber,
      size: pageSize,
      sortField,
      sortOrder,
    }),
    [filters, mapFiltersToCriteriaFn, pageNumber, pageSize, sortField, sortOrder],
  );

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'sendDate');
    setSortOrder(event.sortOrder === 1 ? 1 : -1);
  }, []);

  const { messages, total, isLoading } = useFormattedMessages(repository!, criteria);

  const handlePageChange = (e: DataTablePageEvent) => {
    setPageNumber(Math.floor(e.first / e.rows));
    setPageSize(e.rows);
  };

  return {
    filters,
    setFilters,
    messages,
    total,
    loadingRepository: loadingRepository || isLoading,
    pageNumber,
    pageSize,
    handlePageChange,
    sortField,
    sortOrder,
    handleSort,
  };
}
